import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { AutocompleteItemWrapper, DotsLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import useTruncatedTextTooltip from "app/pages/sales-intelligence/hooks/useTruncatedTextTooltip";
import type { IDomainItem } from "app/@types/autocomplete";

import highlightQueryWords from "../../utils/highlightQueryWords";
import { StyledAutocompleteItemText, StyledAutocompleteItemTextWrapper } from "../CompanyAutocompleteItem/styles";
import NoSearchResults from "../NoSearchResults/NoSearchResults";

import { StyledCompanyAutocomplete, StyledCompanyPlaceholderText } from "./styles";

const DomainByCompanyAutocomplete = ({
    domains,
    isLoading,
    onWebsiteClick,
}: {
    domains?: IDomainItem[];
    isLoading?: boolean;
    onWebsiteClick?: (domain: string) => void;
}) => {
    const { isTooltipEnabled, refCallback } = useTruncatedTextTooltip();

    const handleWebsiteClick = (newDomain: string) => {
        onWebsiteClick?.(newDomain);
    };

    const handleGetListItems = (query: string) => {
        const term = query.trim();

        const filteredDomains = domains?.filter((domain) => domain.name.toLowerCase().includes(term.toLowerCase()));

        if (!filteredDomains || filteredDomains.length === 0) {
            return <NoSearchResults searchTerm={term} />;
        }

        const cleanedTerm = term.toLowerCase().replace(/,/g, " ");
        const queryWords = cleanedTerm.split(" ").filter(Boolean);

        return filteredDomains.map(({ name }) => (
            <AutocompleteItemWrapper
                onClick={() => handleWebsiteClick(name)}
                key={name}
            >
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    width="32px"
                    height="32px"
                    borderRadius="6px"
                    border={`1px solid ${colorsPalettes.carbon[50]}`}
                    background={colorsPalettes.carbon[0]}
                    boxShadow="0 2px 4px 0 rgba(#cacaca, 0.2)"
                >
                    <SWReactIcons
                        iconName="website-analysis"
                        size="xs"
                        iconColor={colorsPalettes.carbon[300]}
                    />
                </Flex>
                <Tooltip
                    content={name}
                    placement="top"
                    enabled={isTooltipEnabled}
                >
                    <StyledAutocompleteItemTextWrapper>
                        <StyledAutocompleteItemText ref={refCallback}>
                            {highlightQueryWords(name, queryWords)}
                        </StyledAutocompleteItemText>
                    </StyledAutocompleteItemTextWrapper>
                </Tooltip>
            </AutocompleteItemWrapper>
        ));
    };

    return (
        <StyledCompanyAutocomplete
            placeholder={
                <StyledCompanyPlaceholderText>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate("si.company-analysis.domain.autocomplete.placeholder")}
                    </Text>
                </StyledCompanyPlaceholderText>
            }
            loadingComponent={<DotsLoader />}
            floating={true}
            debounce={250}
            getListItems={handleGetListItems}
            isLoading={isLoading}
            autoFocus
        />
    );
};

export default DomainByCompanyAutocomplete;
